import { useState } from "react";
import { motion } from "framer-motion";
import SectionHeader from "@/components/SectionHeader";
import SquareCardForm, { useSquareCard } from "@/components/SquareCardForm";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createCateringDeposit } from "@/lib/api";

const CARD_ID = "sq-deposit-card";

const PRESET_AMOUNTS = [
    { label: "$200", value: 200 },
    { label: "$500", value: 500 },
    { label: "$1,000", value: 1000 },
    { label: "Custom", value: null },
];

export default function PayDepositPage() {
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selected, setSelected] = useState(200);
    const [custom, setCustom] = useState("");
    const [form, setForm] = useState({
        name: "", email: "", phone: "", event_date: "", inquiry_id: "", notes: "",
    });
    const { ready: cardReady, configured, tokenize } = useSquareCard(CARD_ID);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const depositAmount = selected !== null ? selected : (parseFloat(custom) || 0);

    const submit = async () => {
        if (!form.name || !form.email || !form.phone || !form.event_date) {
            toast.error("Name, email, phone, and event date are required.");
            return;
        }
        if (depositAmount < 50) {
            toast.error("Minimum deposit is $50.");
            return;
        }
        if (configured && !cardReady) {
            toast.error("Card form is still loading. Please wait.");
            return;
        }
        setSubmitting(true);
        try {
            const sourceId = configured ? await tokenize() : "PLACEHOLDER_NOT_CONFIGURED";
            await createCateringDeposit({
                customer_name: form.name,
                customer_email: form.email,
                customer_phone: form.phone,
                event_date: form.event_date,
                deposit_amount: depositAmount,
                inquiry_id: form.inquiry_id || "",
                notes: form.notes,
                square_source_id: sourceId,
            });
            setSubmitted(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Payment failed. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Payment failed.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div data-testid="pay-deposit-page">
                <section className="section">
                    <div className="container-narrow max-w-xl mx-auto text-center">
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                            <CheckCircle2 size={56} className="text-orange-300 mx-auto mb-4" />
                            <div className="font-display text-3xl text-white mb-2">Deposit Received</div>
                            <p className="text-white/70 mb-2">
                                ${depositAmount.toFixed(2)} deposit confirmed for{" "}
                                <span className="text-white">{form.name}</span>.
                            </p>
                            <p className="text-white/55 text-sm">Mo will reach out to finalize the details for your {form.event_date} event.</p>
                        </motion.div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div data-testid="pay-deposit-page">
            <section className="section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="Secure Your Date"
                        title="Pay a Catering Deposit"
                        subtitle="Lock in your event date with a deposit. The remainder is due on the day — Mo will confirm all details by phone."
                    />

                    <div className="max-w-xl">
                        <Card className="surface-card border-0">
                            <CardContent className="p-6 sm:p-8 space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="sm:col-span-2">
                                        <Label className="text-white/80">Your Name *</Label>
                                        <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="First and last name" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                    </div>
                                    <div>
                                        <Label className="text-white/80">Email *</Label>
                                        <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                    </div>
                                    <div>
                                        <Label className="text-white/80">Phone *</Label>
                                        <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 555-5555" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                    </div>
                                    <div>
                                        <Label className="text-white/80">Event Date *</Label>
                                        <Input type="date" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} className="mt-1 bg-black/40 border-white/10" />
                                    </div>
                                    <div>
                                        <Label className="text-white/80">Inquiry # (optional)</Label>
                                        <Input value={form.inquiry_id} onChange={(e) => update("inquiry_id", e.target.value)} placeholder="From your booking confirmation" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label className="text-white/80">Notes</Label>
                                        <Textarea rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Event details, questions, anything else..." className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                    </div>
                                </div>

                                {/* Deposit amount */}
                                <div>
                                    <Label className="text-white/80 block mb-2">Deposit Amount</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {PRESET_AMOUNTS.map(({ label, value }) => (
                                            <button
                                                key={label}
                                                onClick={() => { setSelected(value); if (value !== null) setCustom(""); }}
                                                className={`px-4 py-2 rounded-md border text-sm font-display tracking-[0.1em] transition-colors ${
                                                    selected === value
                                                        ? "bg-orange-500/20 border-orange-400/50 text-orange-300"
                                                        : "border-white/15 text-white/70 hover:border-white/30"
                                                }`}
                                            >{label}</button>
                                        ))}
                                    </div>
                                    {selected === null && (
                                        <Input
                                            type="number"
                                            min="50"
                                            value={custom}
                                            onChange={(e) => setCustom(e.target.value)}
                                            placeholder="Enter amount (min $50)"
                                            className="mt-2 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40 max-w-xs"
                                        />
                                    )}
                                    {depositAmount >= 50 && (
                                        <div className="mt-2 text-orange-300 font-display text-lg">
                                            Total: ${depositAmount.toFixed(2)}
                                        </div>
                                    )}
                                </div>

                                {/* Payment */}
                                <div>
                                    <Label className="text-white/80 block mb-2">Payment</Label>
                                    <SquareCardForm containerId={CARD_ID} />
                                </div>

                                <button
                                    className="ember-btn w-full py-3 rounded-md font-display tracking-[0.12em] uppercase text-sm flex items-center justify-center gap-2"
                                    onClick={submit}
                                    disabled={submitting}
                                >
                                    {submitting
                                        ? <><Loader2 size={14} className="animate-spin" /> Processing…</>
                                        : depositAmount >= 50
                                            ? `Pay $${depositAmount.toFixed(2)} Deposit`
                                            : "Pay Deposit"
                                    }
                                </button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
}
